import { isNil } from 'lodash';
import type {
  ChangeEvent,
  FocusEvent,
  InputHTMLAttributes,
  ReactNode,
} from 'react';
import { forwardRef, memo, useCallback, useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import { CURRENCY_MAP } from '../../utils/currencies';
import { getTooltipText } from '../../utils/get-tooltip-text';
import { slugify } from '../../utils/slugify';
import { TypographyVariant } from '../../utils/theme';
import Clear from '../deprecated-select/components/clear';
import type { ISelectOption } from '../deprecated-select/select';
import type { IconName } from '../icon/icon';
import InputFeedback from '../input-feedback';
import InputLabelWithHint from '../input-label/input-label-with-hint';
import CurrencyPrefix from './components/currency-prefix';
import { getIconColor } from './helper-functions/colors';
import { getValidationError } from './helper-functions/errors';
import {
  ClearWrapper,
  Counter,
  CurrencySelect,
  IconWrapper,
  InputWrapper,
  RightSection,
  StyledIcon,
  StyledInput,
  TopRightSection,
} from './input.styles';
interface IInputValidationError {
  hasError: boolean;
  errorMessage: string;
}

export enum InputVariant {
  Text = 'text',
  TextArea = 'textarea',
  Amount = 'amount',
  Icon = 'icon',
}

interface IInputBase extends InputHTMLAttributes<HTMLInputElement> {
  name: string;
  value?: string | number;
  variant?: InputVariant;
  maxLength?: number;
  hideMaxLength?: boolean;
  showLabel?: boolean;
  hasError?: boolean;
  defaultValue?: string | number;
  errorMessage?: string;
  warningMessage?: string;
  successMessage?: string;
  infoMessage?: string;
  onCurrencyChange?: (currency: string) => void;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  min?: number | string;
  max?: number | string;
  width?: string;
  height?: string;
  validationErrorCallback?: (error: IInputValidationError) => void;
  currentValue?: string;
  icon?: IconName;
  hintText?: string | ReactNode;
  clearable?: boolean;
  maxLengthCounterDescription?: string;
  topRightSection?: ReactNode;
  rightSection?: ReactNode;
}

type VariantProps =
  | {
      variant?: InputVariant.Amount;
      currency?: string;
      currencyList?: ISelectOption[];
      defaultCurrency?: string;
    }
  | {
      variant?: InputVariant.Text;
      currency?: never;
      currencyList?: never;
      defaultCurrency?: never;
    }
  | {
      variant?: InputVariant.TextArea;
      currency?: never;
      currencyList?: never;
      defaultCurrency?: never;
    }
  | {
      variant?: InputVariant.Icon;
      currency?: never;
      currencyList?: never;
      defaultCurrency?: never;
    };

export type IInput = IInputBase & VariantProps;

const Input = forwardRef<HTMLInputElement, IInput>(
  (
    {
      id,
      type = 'text',
      hasError,
      width,
      name,
      showLabel = true,
      currency,
      currencyList,
      defaultCurrency,
      variant = InputVariant.Text,
      onCurrencyChange,
      onChange,
      onBlur,
      onFocus,
      maxLength,
      hideMaxLength = false,
      defaultValue,
      value,
      validationErrorCallback,
      disabled = false,
      icon,
      hintText,
      clearable,
      maxLengthCounterDescription,
      step,
      errorMessage,
      warningMessage,
      successMessage,
      infoMessage,
      topRightSection,
      rightSection,
      ...props
    },
    ref
  ) => {
    const theme = useTheme();
    const uniqueId = useId();

    const { t } = useTranslation();

    /**
     * If id is not provided, generate one from name
     * In general id cannot have spaces, so replace them with dashes
     */
    const inputId = id ? slugify(String(id)) : uniqueId;

    const [validationError, setValidationError] = useState({
      hasError: false,
      errorMessage: '',
    });
    const [isFocused, setIsFocused] = useState(false);
    const [isChanged, setIsChanged] = useState(false);

    const handleSetValidationError = useCallback(
      (error: IInputValidationError) => {
        setValidationError(error);
        if (validationErrorCallback) validationErrorCallback(error);
      },
      [validationErrorCallback]
    );
    const [uncontrolledValue, setUncontrolledValue] = useState<string | number>(
      defaultValue || ''
    );

    const [selectedCurrency, setSelectedCurrency] = useState(defaultCurrency);
    const selectedCurrencySymbol =
      selectedCurrency &&
      (CURRENCY_MAP[selectedCurrency as keyof typeof CURRENCY_MAP] ?? '');

    const isAmount = variant === InputVariant.Amount;
    const inputType = isAmount ? 'number' : type;
    const showCurrencySelect = isAmount && Boolean(currencyList?.length);

    const isControlled = Boolean(value !== undefined);
    const resolvedValue = isControlled ? value : uncontrolledValue;

    // For amount inputs, format the initially rendered amount, to add missing decimal places
    const currentValue =
      isAmount && !isChanged ? formatAmount(resolvedValue) : resolvedValue;

    const ariaDescribedBy = useMemo(() => {
      const isError = hasError || validationError.hasError;
      if (isError && isAmount) return `${inputId}-currency ${inputId}-error`;

      if (isError && !isAmount && maxLength)
        return `${inputId}-counter ${inputId}-error`;

      if (isError) return `${inputId}-error`;
      if (isAmount) return `${inputId}-currency`;
      if (maxLength) return `${inputId}-counter`;
      return hintText ? getTooltipText(name) : undefined;
    }, [
      hasError,
      hintText,
      inputId,
      isAmount,
      maxLength,
      name,
      validationError.hasError,
    ]);

    const handleOnFocus: (event: FocusEvent<HTMLInputElement>) => void = (
      event: FocusEvent<HTMLInputElement>
    ): void => {
      onFocus?.(event);
      setIsFocused(true);
    };

    const handleOnBlur: (event: FocusEvent<HTMLInputElement>) => void = (
      event: FocusEvent<HTMLInputElement>
    ): void => {
      onBlur?.(event);
      if (isAmount) {
        const value = formatAmount(event.target.value);
        event.target.value = value;
        !isControlled && setUncontrolledValue(value);
        onChange?.(event);
        setIsChanged(true);
      }

      if (props.required && !event.target.value) {
        handleSetValidationError({
          hasError: true,
          errorMessage: t('phrases.requiredField', {
            defaultValue: 'This field cannot be blank',
          }),
        });
      }

      setIsFocused(false);
    };

    const handleOnChange: (event: ChangeEvent<HTMLInputElement>) => void =
      useCallback(
        (event: ChangeEvent<HTMLInputElement>): void => {
          !isControlled && setUncontrolledValue(event.target.value);
          onChange?.(event);
          setIsChanged(true);
          const newValidationError = getValidationError(
            Number.parseFloat(event.target.value),
            isAmount,
            props?.min,
            props?.max
          );
          handleSetValidationError(newValidationError);
        },
        [
          isControlled,
          onChange,
          handleSetValidationError,
          isAmount,
          props?.min,
          props?.max,
        ]
      );

    return (
      <InputWrapper width={width}>
        <InputLabelWithHint
          inputId={inputId}
          showLabel={showLabel}
          hintText={hintText}
          hasError={hasError}
          name={name}
          validationError={validationError}
        />
        {showCurrencySelect && (
          <CurrencySelect
            name={name}
            selectedVariant={TypographyVariant.bodyMd2}
            options={currencyList || []}
            showLabel={false}
            $hasError={hasError || validationError.hasError}
            $symbol={selectedCurrencySymbol}
            defaultValue={defaultCurrency}
            onChange={(value) => {
              setSelectedCurrency(value as string);
              onCurrencyChange?.(value as string);
            }}
            width="80px"
            disabled={disabled}
            hasRightDivider
            hasError={hasError || validationError.hasError}
            data-testid={`currency-select-${name}`}
          />
        )}
        {icon && (
          <IconWrapper>
            <StyledIcon
              name={icon}
              color={getIconColor(
                hasError || validationError.hasError,
                disabled,
                theme
              )}
            />
          </IconWrapper>
        )}
        <StyledInput
          onFocus={handleOnFocus}
          onBlur={handleOnBlur}
          id={inputId}
          type={inputType}
          step={step ?? (isAmount ? 'any' : undefined)} // If step is passed through props, use it, otherwise use 'any' for amount inputs and undefined for other inputs
          ref={ref}
          $hasError={hasError || validationError.hasError}
          variant={variant}
          width={width}
          as={variant === InputVariant.TextArea ? 'textarea' : 'input'}
          $currency={selectedCurrency ?? currency}
          $showCurrencySelect={showCurrencySelect}
          value={currentValue ?? undefined}
          onChange={handleOnChange}
          disabled={disabled}
          maxLength={maxLength}
          className="fs-mask"
          onWheel={(event: React.WheelEvent<HTMLInputElement>) =>
            event.currentTarget.blur()
          }
          data-testid={`input-${name}`}
          {...props}
          placeholder={InputVariant.Icon === variant ? props.placeholder : ''}
          aria-describedby={ariaDescribedBy}
          aria-invalid={hasError || validationError.hasError}
        />
        {topRightSection && (
          <TopRightSection>{topRightSection}</TopRightSection>
        )}
        {rightSection && <RightSection>{rightSection}</RightSection>}
        {maxLength && !isAmount && !hideMaxLength && (
          <Counter
            variant={TypographyVariant.labelSm}
            $hasError={hasError || validationError.hasError}
            $isDisabled={disabled}
            id={`${inputId}-counter`}
            aria-label={
              maxLengthCounterDescription ||
              t('phrases.maxLengthCounterDescription', {
                maxLength,
              })
            }
          >
            {String(currentValue).length}/{maxLength}
            <span style={{ display: 'none' }} aria-live="polite">
              {maxLength - String(currentValue ?? '').length} characters
              remaining
            </span>
          </Counter>
        )}
        {isAmount && currency && !currencyList?.length && (
          <CurrencyPrefix
            currency={currency}
            symbol={CURRENCY_MAP[currency as keyof typeof CURRENCY_MAP]}
            hasError={hasError || validationError.hasError}
            isFocused={isFocused}
            disabled={disabled}
            height={props.height}
            aria-label={t('phrases.currencyDescription', {
              currency,
            })}
          />
        )}
        <InputFeedback
          errorMessage={
            hasError || validationError.hasError
              ? errorMessage || validationError.errorMessage
              : undefined
          }
          warningMessage={warningMessage}
          successMessage={successMessage}
          infoMessage={infoMessage}
          id={`${inputId}-error`}
        />

        {clearable && currentValue && (
          <ClearWrapper>
            <Clear
              handleClear={() =>
                handleOnChange({
                  target: { value: '' },
                } as ChangeEvent<HTMLInputElement>)
              }
            />
          </ClearWrapper>
        )}
      </InputWrapper>
    );
  }
);

Input.displayName = 'Input';

export default memo(Input);

const formatAmount = (value?: string | number) => {
  if (isNil(value) || value === '') {
    return '';
  }
  return Number.parseFloat(String(value)).toFixed(2).replace('.00', '');
};
